import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

/**
 * ImageGallery Component
 * Displays tour images in a grid with lightbox viewer (only for thumbnails)
 *
 * @param {Object} props
 * @param {Array<string>} props.images - Array of additional image URLs
 * @param {string} props.tourTitle - Tour title for alt text
 */
export default function ImageGallery({ images = [], tourTitle }) {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Only use additional images for lightbox
  const galleryImages = images.filter(Boolean);

  // Format images for lightbox
  const lightboxSlides = galleryImages.map((img) => ({
    src: img,
    alt: tourTitle,
  }));

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  if (galleryImages.length === 0) {
    return null; // Don't show anything if no additional images
  }

  return (
    <div className="space-y-4">
      {/* Thumbnail Grid (if there are more images) */}
      {galleryImages.length > 0 && (
        <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
          {galleryImages.slice(0, 6).map((img, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group border-2 border-transparent hover:border-emerald-500 transition-all duration-300"
              onClick={() => openLightbox(index)}
            >
              <img
                src={img}
                alt={`${tourTitle} - Image ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}

          {/* "View All" button if more than 6 images */}
          {galleryImages.length > 6 && (
            <div
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group bg-slate-900 dark:bg-slate-700 flex items-center justify-center border-2 border-slate-700 dark:border-slate-600 hover:border-emerald-500 transition-all duration-300"
              onClick={() => openLightbox(6)}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  +{galleryImages.length - 6}
                </div>
                <div className="text-xs text-slate-300">More</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        slides={lightboxSlides}
        index={photoIndex}
        on={{
          view: ({ index }) => setPhotoIndex(index),
        }}
        animation={{ fade: 300 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }}
      />
    </div>
  );
}
